import React, { useCallback, useState } from 'react';

import { Collapse, Grid, Radio, RadioGroup, TableCell, TableRow } from '@material-ui/core';

import { ISubmitRefund, ISubmitRefundArgs } from '../../../store/admin/clubs/actions';
import { IClubTransaction } from '../../../store/admin/clubs/store';

import Button from '../../../components/Button';
import Input from '../../../components/Input';
import { LabeledBox } from '../../../components/LabeledBox/LabeledBox';

import { ITransactionItem, TransactionItems } from './TransactionItems';

interface IExpandedTransactionRow {
  clubId: number;
  collapsed: boolean;
  submitRefund: ISubmitRefund;
  transaction: IClubTransaction;
}

export const ExpandedTransactionRow = (props: IExpandedTransactionRow) => {
  const {
    collapsed,
    clubId,
    submitRefund,
    transaction: {
      id: transactionId,
      amount,
      amountRefunded,
      processorId,
      description,
      summary: {
        items: { addons, clubMembershipPrice, eventPrice },
        coupon
      }
    }
  } = props;

  const [selectRefundType, setSelectRefundType] = useState<string>('full');
  const [partialRefundValue, setPartialRefundValue] = useState<number>(0);

  const addonsAreProvided: boolean = addons && addons.length > 0;
  const couponIsProvided: boolean = coupon && Object.keys(coupon).length > 0;

  const fullRefundValue = Number(amount) - Number(amountRefunded);
  const fullRefundValueFormatted = `$ ${fullRefundValue.toFixed(2)}`;

  const onSubmitRefund = useCallback(async () => {
    if (submitRefund) {
      const refund: ISubmitRefundArgs = {
        clubId,
        refund: {
          refundAmount: selectRefundType === 'full' ? fullRefundValue : partialRefundValue
        },
        transactionId
      };

      await submitRefund(refund);
    }
  }, [selectRefundType, partialRefundValue]);

  const items: ITransactionItem[] = [];

  if (eventPrice && Object.keys(eventPrice)) {
    items.push({ id: eventPrice.id, label: eventPrice.name, value: eventPrice.price });
  }

  if (clubMembershipPrice) {
    items.push({
      id: 'clubMembershipPrice',
      label: 'Club Membership Price',
      value: clubMembershipPrice
    });
  }

  if (amountRefunded && Number(amountRefunded) > 0) {
    items.push({
      id: 'amountRefunded',
      label: 'Amount Refunded',
      value: `(${amountRefunded})`
    });
  }

  return (
    <TableRow className="expandable-row">
      <TableCell className="inner-cell" colSpan={7}>
        <Collapse in={collapsed} timeout="auto" unmountOnExit={true}>
          <Grid className="grid" container={true} spacing={8}>
            <Grid className="grid-item" item={true} xs={12} md={4}>
              <LabeledBox label="Transaction number">{processorId}</LabeledBox>
              <LabeledBox label="Note">
                {description ? (
                  description
                ) : (
                  <TransactionItems label="No notes to show" items={[]} />
                )}
              </LabeledBox>
            </Grid>
            <Grid className="grid-item" item={true} xs={12} md={4}>
              <LabeledBox label="Items">
                <>
                  <TransactionItems label="Items" items={items} />
                  {addonsAreProvided && (
                    <TransactionItems
                      label="Add on"
                      items={addons.map((addon: any) => ({
                        id: addon.id,
                        label: addon.name,
                        value: addon.price
                      }))}
                    />
                  )}
                  {couponIsProvided && (
                    <TransactionItems
                      label="Coupon code"
                      items={[
                        {
                          id: coupon.codename,
                          label: coupon.title,
                          value: `(${coupon.discountValue})`
                        }
                      ]}
                    />
                  )}
                </>
              </LabeledBox>
            </Grid>
            <Grid className="grid-item" item={true} xs={12} md={4}>
              <LabeledBox label="Issue refund">
                <RadioGroup className="radio-group-inner" value={selectRefundType}>
                  <div
                    className={`radio-item-container ${
                      selectRefundType === 'full' ? 'active' : ''
                    }`}
                  >
                    <div className="label-container">
                      <Radio
                        checked={selectRefundType === 'full'}
                        onChange={(event: any) => setSelectRefundType(event.target.value)}
                        value="full"
                        name="radio-button"
                        className="radio-button"
                        inputProps={{ 'aria-label': 'full' }}
                      />
                      <span className="label">Full refund</span>
                    </div>

                    <p className="refund-value">{fullRefundValueFormatted}</p>
                  </div>
                  <div
                    className={`radio-item-container ${
                      selectRefundType === 'partial' ? 'active' : ''
                    }`}
                  >
                    <div className="label-container">
                      <Radio
                        checked={selectRefundType === 'partial'}
                        onChange={(event: any) => setSelectRefundType(event.target.value)}
                        value="partial"
                        name="radio-button"
                        className="radio-button"
                        inputProps={{ 'aria-label': 'partial' }}
                      />
                      <span className="label">Partial</span>
                    </div>
                    <Input
                      label=""
                      disabled={selectRefundType === 'full'}
                      type="number"
                      className="refund-value"
                      noIcon={true}
                      value={partialRefundValue}
                      onChange={event => {
                        setPartialRefundValue(Number(event.target.value));
                      }}
                    />
                  </div>
                  <div className="action-buttons full-width">
                    <Button
                      style={{ width: 'initial' }}
                      color="primary"
                      type="button"
                      onClick={onSubmitRefund}
                      disabled={
                        selectRefundType === 'partial' &&
                        (partialRefundValue < 0 || partialRefundValue > fullRefundValue)
                      }
                      variant="contained"
                    >
                      SUBMIT REFUND
                    </Button>
                    <Button
                      style={{ width: 'initial' }}
                      color="primary"
                      type="button"
                      onClick={() => {
                        setPartialRefundValue(0);
                        setSelectRefundType('full');
                      }}
                    >
                      CANCEL
                    </Button>
                  </div>
                </RadioGroup>
              </LabeledBox>
            </Grid>
          </Grid>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};
